/**
 * Whether a logout has started on this page.
 *
 * `logout()` ends by assigning `document.location`, which does not unload synchronously —
 * renders and in-flight requests keep running until the new document commits, and every
 * credentialed request in that window 401s. Anything that would answer those 401s by
 * changing route or nagging the user must check this first.
 *
 * Import-free leaf so the router and the app root can both read it without recreating the
 * `SessionStore -> authentication -> notifier` cycle.
 */
/**
 * The suppression only has to cover the navigation `logout()` just requested, and expires
 * afterwards because that navigation can be cancelled: `document.location = …` fires
 * `beforeunload`, and the draft-guess, lockdown, confirm-leave and grading-draft prompts all
 * arm it — a user who clicks "Stay" keeps a live document, which must not be left permanently
 * unable to navigate.
 *
 * Held as a deadline rather than a `setTimeout`, so there is no handle to leave dangling: a
 * retry after a failed sign-out gets its own full window instead of inheriting the previous
 * attempt's countdown. Measured on `performance.now()`, which is monotonic — a wall-clock step
 * (NTP, manual change) while the window is open must not stretch it.
 *
 * ponytail: a fixed window, not unload bookkeeping. Overshooting it only restores the old
 * flash for a logout still in flight after 10s; per-navigation tracking if that shows up.
 */
const NAVIGATION_WINDOW_MS = 10_000;

let latchedUntil = 0;

export function setLoggingOut(): void {
  latchedUntil = performance.now() + NAVIGATION_WINDOW_MS;
}

/** Exported for the sign-out failure path, which releases it sooner than the window would. */
export function clearLoggingOut(): void {
  latchedUntil = 0;
}

export function isLoggingOut(): boolean {
  return performance.now() < latchedUntil;
}
